import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import PropagateLoader from 'react-spinners/PropagateLoader';

import { Message } from 'components/Message';
import { Pulse } from 'components/Pulse';
import { CandidateAI, ChatMessage, ChatSession } from 'types/types';

export function GenerateCandidateAdminOnlyNotice(): React.ReactElement {
  return <Box>You must be logged in as an admin to generate AI candidates.</Box>;
}

export function GenerateCandidateProcessing({
  processing,
  processingMessage,
  chatSession,
}: {
  processing: boolean;
  processingMessage: ChatMessage | null;
  chatSession: ChatSession | null;
}): React.ReactNode {
  if (!processing) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        m: 2,
      }}
    >
      {processingMessage && chatSession && (
        <Message message={processingMessage} chatSession={chatSession} />
      )}
      <PropagateLoader
        size="10px"
        loading={processing}
        aria-label="Loading Spinner"
        data-testid="loader"
      />
    </Box>
  );
}

export function GenerateCandidateImageControls({
  generatedUser,
  processing,
  canGenImage,
  timestamp,
  onGenerate,
}: {
  generatedUser: CandidateAI | null;
  processing: boolean;
  canGenImage: boolean;
  timestamp: string;
  onGenerate: () => void;
}): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            width: 'min-content',
            height: 'min-content',
          }}
        >
          <Avatar
            src={
              generatedUser?.profileImage
                ? `/api/1.0/candidates/profile/${generatedUser.username}`
                : ''
            }
            alt={`${generatedUser?.fullName}'s profile`}
            sx={{ width: 80, height: 80, border: '2px solid #e0e0e0' }}
          />
          {processing && (
            <Pulse
              sx={{ position: 'relative', left: '-80px', top: '0px', mr: '-80px' }}
              timestamp={timestamp}
            />
          )}
        </Box>

        <Tooltip title={`${generatedUser?.profileImage ? 'Re-' : ''}Generate Picture`}>
          <span style={{ display: 'flex', flexGrow: 1 }}>
            <Button
              sx={{
                m: 1,
                gap: 1,
                justifySelf: 'flex-start',
                alignSelf: 'center',
                flexGrow: 0,
                maxHeight: 'min-content',
              }}
              variant="contained"
              disabled={processing || !canGenImage}
              onClick={onGenerate}
            >
              {generatedUser?.profileImage ? 'Re-' : ''}Generate Picture
              <SendIcon />
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function GenerateCandidateActions({
  processing,
  canCancel,
  onSend,
  onCancel,
}: {
  processing: boolean;
  canCancel: boolean;
  onSend: () => void;
  onCancel: () => void;
}): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row' }}>
      <Tooltip title={'Send'}>
        <span style={{ display: 'flex', flexGrow: 1 }}>
          <Button
            sx={{ m: 1, gap: 1, flexGrow: 1 }}
            variant="contained"
            disabled={processing}
            onClick={onSend}
          >
            Generate New Persona
            <SendIcon />
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Cancel">
        <span style={{ display: 'flex' }}>
          <IconButton
            aria-label="cancel"
            onClick={onCancel}
            sx={{ display: 'flex', margin: 'auto 0px' }}
            size="large"
            edge="start"
            disabled={!canCancel}
          >
            <CancelIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
